import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import logoImg from "../assets/logo.png";

const headlineWords = ["One", "consultancy.", "Every", "industry", "that", "matters."];

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center justify-center pt-28 pb-16"
    >
      <div aria-hidden className="absolute inset-0 soft-mesh" />
      <div aria-hidden className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />

      <div
        aria-hidden
        className="absolute -top-24 -right-24 h-[320px] w-[320px] rounded-full blur-3xl opacity-60 animate-float"
        style={{ background: "radial-gradient(circle, rgba(0,180,240,0.22), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -left-24 h-[360px] w-[360px] rounded-full blur-3xl opacity-50 animate-float"
        style={{ background: "radial-gradient(circle, rgba(29,127,216,0.18), transparent 70%)", animationDelay: "3s" }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center mb-8"
        >
          <img
            src={logoImg}
            alt="DMASKUT Consultancy"
            className="h-20 md:h-28 w-auto select-none drop-shadow-[0_10px_30px_rgba(0,180,240,0.25)]"
            draggable="false"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.5 }}
          className="inline-flex items-center gap-2 mb-6"
        >
          <span className="h-px w-8 bg-foreground/30" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground font-medium">
            Consultancy · Established 2021
          </span>
          <span className="h-px w-8 bg-foreground/30" />
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display font-bold text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] leading-[1.02] tracking-tighter max-w-5xl mx-auto"
        >
          {headlineWords.map((word, wi) => (
            <motion.span
              key={wi}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.25 + wi * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className={`inline-block mr-[0.22em] ${
                word === "industry" || word === "consultancy." ? "text-gradient-cyan" : ""
              }`}
            >
              {word}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.95, duration: 0.6 }}
          className="mt-7 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed"
        >
          Since 2021, DMASKUT has been the quiet force behind growing businesses —
          handling tax, legal, real estate, IT, education, marketing, manufacturing and finance,
          so you can focus on what you actually do best.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1, duration: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <button
            data-testid="cta-explore"
            onClick={() => scrollTo("domains")}
            className="btn-primary group"
          >
            Explore what we do
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="cta-get-started"
            onClick={() => scrollTo("contact")}
            className="btn-outline"
          >
            Talk to us
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
